import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { MessageSquare, Plus, Edit, Trash2, ToggleLeft, ToggleRight, LogOut } from 'lucide-react';
import { chatbotService } from '../services/chatbotService';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  color: #ffffff;
  font-size: 2rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Button = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  }
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const IntentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const IntentCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-2px);
  }
`;

const IntentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const IntentName = styled.h3`
  color: #ffffff;
  font-size: 1.25rem;
  font-weight: 600;
`;

const StatusToggle = styled.button`
  background: ${props => props.isActive ? '#10b981' : '#ef4444'};
  color: white;
  border: none;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
`;

const KeywordsContainer = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Keywords = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Keyword = styled.span`
  background: rgba(102, 126, 234, 0.2);
  color: #a5b4fc;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
`;

const ResponseText = styled.div`
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const IconButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  border-radius: 16px;
  padding: 2rem;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormLabel = styled.label`
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const Textarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
`;

const ChatbotManagement = ({ showMessage }) => {
  const [intents, setIntents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingIntent, setEditingIntent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    intentName: '',
    keywords: '',
    responseText: '',
    isActive: true
  });

  const loadIntents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await chatbotService.getIntents();
      setIntents(data);
    } catch (error) {
      showMessage('Failed to load chatbot intents', 'error');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    loadIntents();
  }, [loadIntents]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const keywords = formData.keywords.split(',').map(k => k.trim()).filter(k => k);
      
      if (editingIntent) {
        await chatbotService.updateIntent(editingIntent.id, {
          ...formData,
          keywords
        });
        showMessage('Intent updated successfully', 'success');
      } else {
        await chatbotService.createIntent({
          ...formData,
          keywords
        });
        showMessage('Intent created successfully', 'success');
      }
      
      setShowModal(false);
      setEditingIntent(null);
      setFormData({
        intentName: '',
        keywords: '',
        responseText: '',
        isActive: true
      });
      loadIntents();
    } catch (error) {
      showMessage('Failed to save intent', 'error');
    }
  };

  const handleEdit = (intent) => {
    setEditingIntent(intent);
    setFormData({
      intentName: intent.intent_name,
      keywords: intent.keywords.join(', '),
      responseText: intent.response_text,
      isActive: intent.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (intent) => {
    if (!window.confirm(`Are you sure you want to delete "${intent.intent_name}"?`)) {
      return;
    }
    
    try {
      await chatbotService.deleteIntent(intent.id);
      showMessage('Intent deleted successfully', 'success');
      loadIntents();
    } catch (error) {
      showMessage('Failed to delete intent', 'error');
    }
  };

  const handleToggleStatus = async (intent) => {
    try {
      await chatbotService.updateIntent(intent.id, {
        intentName: intent.intent_name,
        keywords: intent.keywords,
        responseText: intent.response_text,
        isActive: !intent.is_active
      });
      showMessage(`Intent ${intent.is_active ? 'disabled' : 'enabled'} successfully`, 'success');
      loadIntents();
    } catch (error) {
      showMessage('Failed to update intent status', 'error');
    }
  };

  const filteredIntents = intents.filter(intent =>
    intent.intent_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    intent.keywords.some(keyword => keyword.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return <Container><div style={{ color: 'white' }}>Loading...</div></Container>;
  }

  return (
    <Container>
      <Header>
        <Title>
          <MessageSquare size={32} />
          Chatbot Management
        </Title>
        <Button onClick={() => setShowModal(true)}>
          <Plus size={20} />
          Add Intent
        </Button>
      </Header>

      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Search intents or keywords..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchContainer>

      <IntentGrid>
        {filteredIntents.map((intent) => (
          <IntentCard key={intent.id}>
            <IntentHeader>
              <IntentName>{intent.intent_name}</IntentName>
              <StatusToggle
                isActive={intent.is_active}
                onClick={() => handleToggleStatus(intent)}
              >
                {intent.is_active ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                {intent.is_active ? 'Active' : 'Inactive'}
              </StatusToggle>
            </IntentHeader>
            
            <KeywordsContainer>
              <Label>Keywords:</Label>
              <Keywords>
                {intent.keywords.map((keyword, index) => (
                  <Keyword key={index}>{keyword}</Keyword>
                ))}
              </Keywords>
            </KeywordsContainer>
            
            <Label>Response:</Label>
            <ResponseText>{intent.response_text}</ResponseText>
            
            <Actions>
              <IconButton onClick={() => handleEdit(intent)}>
                <Edit size={16} />
              </IconButton>
              <IconButton onClick={() => handleDelete(intent)}>
                <Trash2 size={16} />
              </IconButton>
            </Actions>
          </IntentCard>
        ))}
      </IntentGrid>

      {showModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                {editingIntent ? 'Edit Intent' : 'Add New Intent'}
              </ModalTitle>
              <IconButton onClick={() => {
                setShowModal(false);
                setEditingIntent(null);
                setFormData({
                  intentName: '',
                  keywords: '',
                  responseText: '',
                  isActive: true
                });
              }}>
                <LogOut size={20} />
              </IconButton>
            </ModalHeader>
            
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <FormLabel>Intent Name</FormLabel>
                <Input
                  type="text"
                  value={formData.intentName}
                  onChange={(e) => setFormData({...formData, intentName: e.target.value})}
                  placeholder="e.g., login_issue"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel>Keywords (comma-separated)</FormLabel>
                <Input
                  type="text"
                  value={formData.keywords}
                  onChange={(e) => setFormData({...formData, keywords: e.target.value})}
                  placeholder="e.g., login, password, access"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel>Response Text</FormLabel>
                <Textarea
                  value={formData.responseText}
                  onChange={(e) => setFormData({...formData, responseText: e.target.value})}
                  placeholder="Enter the response text..."
                  required
                />
              </FormGroup>
              
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                />
                <FormLabel>Active</FormLabel>
              </CheckboxContainer>
              
              <Button type="submit">
                {editingIntent ? 'Update Intent' : 'Create Intent'}
              </Button>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default ChatbotManagement;
